<template>
  <div class="wizard-step-5">
    <!-- Document Section -->
    <div class="form-section">
      <div class="section-header">
        <span class="section-title">{{ t('Dados Fiscais') }}</span>
      </div>

      <div class="form-grid">
        <!-- Documento Fiscal -->
        <div class="form-field full-width" :class="{ 'has-error': documentInvalid, 'has-success': documentValid }">
          <!-- Seletor de tipo de documento (países multi-tipo, ex: Equador) -->
          <div v-if="hasMultipleDocumentTypes" class="document-type-selector">
            <label class="field-label">{{ t('Tipo de Documento') }}</label>
            <div class="doc-type-options">
              <button
                v-for="opt in allowedDocumentOptions"
                :key="opt.type"
                type="button"
                :data-testid="`franquias-wizard-step5-doctype-${opt.type}`"
                class="doc-type-btn"
                :class="{ active: opt.type === selectedDocumentType }"
                @click="setSelectedDocumentType?.(opt.type)">
                <span>{{ opt.flag }}</span>
                <span>{{ opt.label }}</span>
              </button>
            </div>
          </div>
          <label>{{ documentLabel }}</label>
          <input-text
            data-testid="franquias-wizard-step5-cnpj"
            elem-id="cnpj"
            :placeholder="documentPlaceholder"
            v-model="documentDisplayValue"
            @update:modelValue="handleDocumentInput"
            maxlength="18"
          />
          <span v-if="documentInvalid" class="field-error">{{ documentValidationMessage }}</span>
        </div>

        <!-- Inscrição Estadual (apenas Brasil) -->
        <div v-if="countryConfig.hasInscricaoEstadual" class="form-field">
          <label>{{ t(countryConfig.inscricaoEstadualLabel || '') }}</label>
          <input-text
            data-testid="franquias-wizard-step5-inscricao-estadual"
            elem-id="inscricao_estadual"
            :placeholder="t('Digite a inscrição estadual')"
            v-model="localFormData.inscricao_estadual"
            @update:modelValue="handleInputChange"
          />
        </div>
      </div>
    </div>

    <!-- Address Section -->
    <div class="form-section">
      <div class="section-header">
        <span class="section-title">{{ t('Endereço') }}</span>
      </div>

      <div class="form-grid">
        <!-- CEP/ZIP Code -->
        <div v-if="countryConfig.hasPostalCode" class="form-field cep-field">
          <label>{{ t(countryConfig.postalCodeLabel || '') }}</label>
          <div class="cep-input-wrapper">
            <input-text
              data-testid="franquias-wizard-step5-cep"
              elem-id="cep"
              :placeholder="countryConfig.postalCodeMask || t(countryConfig.postalCodeLabel || '')"
              v-model="localFormData.cep"
              :maxlength="countryConfig.postalCodeMask ? countryConfig.postalCodeMask.length : undefined"
              @update:modelValue="handleCEPChange"
            />
            <span v-if="isLoadingCEP" class="cep-loading"><i class="pi pi-spin pi-spinner"></i></span>
          </div>
        </div>

        <!-- Endereço -->
        <div class="form-field full-width">
          <label>{{ t(countryConfig.streetLabel || '') }}</label>
          <input-text
            data-testid="franquias-wizard-step5-endereco"
            elem-id="endereco"
            :placeholder="t(countryConfig.streetLabel || '')"
            v-model="localFormData.endereco"
            @update:modelValue="handleInputChange"
          />
        </div>

        <!-- Número -->
        <div class="form-field narrow">
          <label>{{ t('Número') }}</label>
          <input-text
            data-testid="franquias-wizard-step5-numero"
            elem-id="numero"
            placeholder="0"
            v-model="localFormData.numero"
            maxlength="10"
            @update:modelValue="handleInputChange"
          />
        </div>

        <!-- Complemento -->
        <div class="form-field">
          <label>{{ t(countryConfig.complementLabel || '') }}</label>
          <input-text
            data-testid="franquias-wizard-step5-complemento"
            elem-id="complemento"
            :placeholder="t(countryConfig.complementLabel || '')"
            v-model="localFormData.complemento"
            @update:modelValue="handleInputChange"
          />
        </div>

        <!-- Bairro -->
        <div class="form-field">
          <label>{{ t(countryConfig.neighborhoodLabel || '') }}</label>
          <input-text
            data-testid="franquias-wizard-step5-bairro"
            elem-id="bairro"
            :placeholder="t(countryConfig.neighborhoodLabel || '')"
            v-model="localFormData.bairro"
            @update:modelValue="handleInputChange"
          />
        </div>

        <!-- Cidade -->
        <div class="form-field">
          <label>{{ t('Cidade') }}</label>
          <input-text
            data-testid="franquias-wizard-step5-cidade"
            elem-id="cidade"
            :placeholder="t('Nome da cidade')"
            v-model="localFormData.cidade"
            @update:modelValue="handleInputChange"
          />
        </div>

        <!-- Estado -->
        <div class="form-field narrow">
          <label>{{ t('Estado') }}</label>
          <input-text
            data-testid="franquias-wizard-step5-estado"
            elem-id="estado"
            :placeholder="t('UF')"
            v-model="localFormData.estado"
            maxlength="2"
            @update:modelValue="handleInputChange"
          />
        </div>
      </div>
    </div>

    <!-- Info Card -->
    <div class="info-card">
      <i class="pi pi-info-circle"></i>
      <span>{{ t('O documento fiscal informado será sincronizado com as informações da franquia') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useViaCep } from '../../composables/useViaCep';
import { validateCedulaEC, validateRUC } from '../../utils/documentValidation';
const { lookupCep } = useViaCep();

interface CountryConfig {
  name?: string;
  hasInscricaoEstadual?: boolean;
  inscricaoEstadualLabel?: string;
  hasPostalCode?: boolean;
  postalCodeLabel?: string;
  postalCodeMask?: string;
  streetLabel?: string;
  complementLabel?: string;
  neighborhoodLabel?: string;
  [key: string]: unknown;
}

interface FormDataInput {
  cnpj?: string;
  inscricao_estadual?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  [key: string]: unknown;
}

interface LocalFormData {
  cnpj: string;
  inscricao_estadual: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

interface DocumentOption {
  type: string;
  label?: string;
  flag?: string;
  mask?: string | null;
  placeholder?: string;
}

interface Props {
  formData: FormDataInput;
  selectedDocumentType?: string;
  currentDocumentPresentation?: string;
  setSelectedDocumentType?: (value: string) => void;
  allowedDocumentOptions?: DocumentOption[];
  countryConfig: CountryConfig;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'validation-change': [isValid: boolean];
  'form-update': [data: LocalFormData];
  'cnpj-change': [value: string];
}>();

const { t } = useI18n();

// Alias para countryConfig da prop (para manter o template limpo)
const countryConfig = computed<CountryConfig>(() => props.countryConfig);

// Verifica se é Brasil (para funcionalidade de busca de CEP)
const isBrazil = computed<boolean>(() => {
  return countryConfig.value?.name === 'Brasil';
});

// Seletor de tipo de documento — só aparece quando o país permite mais de um tipo (ex: Equador: cedula_ec, ruc)
const hasMultipleDocumentTypes = computed<boolean>(() => (props.allowedDocumentOptions?.length || 0) > 1);

// Estado para loading da busca de CEP
const isLoadingCEP = ref<boolean>(false);

const localFormData = ref<LocalFormData>({
  cnpj: '',
  inscricao_estadual: '',
  endereco: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
});

const syncFromProps = (): void => {
  if (props.formData) {
    (Object.keys(localFormData.value) as Array<keyof LocalFormData>).forEach((key) => {
      const value = props.formData[key];
      localFormData.value[key] = (value as string | undefined) || '';
    });

    // Inicializar documento
    const cnpjValue = props.formData?.cnpj || '';
    const digits = cnpjValue.replace(/\D/g, '');
    documentRawValue.value = digits;
    documentDisplayValue.value = formatDocument(digits);
  }
};

// ===== LÓGICA DE DOCUMENTO COM MÁSCARA DINÂMICA =====

// Valor bruto (apenas dígitos)
const documentRawValue = ref<string>('');

// Valor formatado para exibição (com máscara)
const documentDisplayValue = ref<string>('');

// Estado de validação
const documentValid = ref<boolean>(false);
const documentInvalid = ref<boolean>(false);
const documentValidationMessage = ref<string>('');

// Label dinâmico baseado no tipo de documento do país selecionado
const documentLabel = computed<string>(() => {
  // Se tem presentation do país, usar ele (ex: "Cédula" para EC, "RUT" para CL)
  if (props.currentDocumentPresentation) {
    return props.currentDocumentPresentation;
  }

  // Fallback baseado nos dígitos
  const digits = documentRawValue.value;
  if (!digits || digits.length === 0) {
    return t('Documento');
  }
  if (digits.length <= 11) {
    return t('CPF');
  }
  return t('CNPJ');
});

// Placeholder dinâmico baseado no país
const documentPlaceholder = computed<string>(() => {
  const type = props.selectedDocumentType;
  if (type === 'cedula_ec') return '0000000000';
  if (type === 'ruc') return '1234567890001';
  if (type === 'rut') return '00.000.000-*';
  if (type === 'dni') return '00000000';
  if (type === 'ci') return '0000000000';
  if (type === 'nif') return '000000000';
  if (type === 'cedula' || type === 'dpi') return '0000000';

  // Fallback CPF/CNPJ
  const digits = documentRawValue.value;
  if (!digits || digits.length === 0) return t('Digite o documento');
  if (digits.length <= 11) return '000.000.000-00';
  return '00.000.000/0000-00';
});

/**
 * Formata documento de acordo com o tipo do país
 */
const formatDocument = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const type = props.selectedDocumentType;

  // Para países não-Brasil, apenas retorna os dígitos sem máscara
  if (type && !['cpf', 'cnpj'].includes(type)) {
    return digits;
  }

  // CPF/CNPJ brasileiro com máscara
  if (digits.length <= 11) {
    return digits.replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  } else {
    return digits.replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }
};

/**
 * Valida CPF usando algoritmo oficial
 */
const validateCPF = (cpf: string): boolean => {
  cpf = cpf.replace(/\D/g, '');
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cpf.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cpf.charAt(10))) return false;

  return true;
};

/**
 * Valida CNPJ usando algoritmo oficial
 */
const validateCNPJ = (cnpj: string): boolean => {
  cnpj = cnpj.replace(/\D/g, '');
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;

  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnpj.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cnpj.charAt(12))) return false;

  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cnpj.charAt(i)) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cnpj.charAt(13))) return false;

  return true;
};

/**
 * Handle input do documento - formata e valida em tempo real
 */
const handleDocumentInput = (value: string | undefined): void => {
  value = value || '';
  // Remove caracteres não numéricos
  const digits = value.replace(/\D/g, '');
  documentRawValue.value = digits;

  // Atualiza valor formatado para exibição
  documentDisplayValue.value = formatDocument(digits);

  // Atualiza formData
  localFormData.value.cnpj = documentDisplayValue.value;

  const type = props.selectedDocumentType;

  // Map de validação por tipo de documento
  const lengthRules: Record<string, { length: number; label: string }> = {
    cpf: { length: 11, label: 'CPF' },
    cnpj: { length: 14, label: 'CNPJ' },
    rut: { length: 0, label: 'RUT' },    // 8-9 chars, validação própria
    dni: { length: 0, label: 'DNI' },     // 7-8 chars
    ci: { length: 0, label: 'CI' },       // 6-10 chars
    cedula: { length: 0, label: 'Cédula' }, // 6-8 chars
  };

  const rule = lengthRules[type || ''];

  // Validação
  if (digits.length === 0) {
    documentValid.value = false;
    documentInvalid.value = false;
    documentValidationMessage.value = '';
  } else if (type === 'cpf' && digits.length === 11) {
    if (validateCPF(digits)) { documentValid.value = true; documentInvalid.value = false; documentValidationMessage.value = ''; }
    else { documentValid.value = false; documentInvalid.value = true; documentValidationMessage.value = t('CPF inválido'); }
  } else if (type === 'cnpj' && digits.length === 14) {
    if (validateCNPJ(digits)) { documentValid.value = true; documentInvalid.value = false; documentValidationMessage.value = ''; }
    else { documentValid.value = false; documentInvalid.value = true; documentValidationMessage.value = t('CNPJ inválido'); }
  } else if (type === 'cedula_ec' && digits.length === 10) {
    // Cédula equatoriana: algoritmo Mod-10 + província compartilhado com FranquiasForm/InputDocument
    const result = validateCedulaEC(digits, t);
    documentValid.value = result.valid;
    documentInvalid.value = !result.valid;
    documentValidationMessage.value = result.error || '';
  } else if (type === 'ruc' && digits.length === 13) {
    // RUC equatoriano: Mod-10/Mod-11 por tipo de contribuinte compartilhado com FranquiasForm/InputDocument
    const result = validateRUC(digits, t);
    documentValid.value = result.valid;
    documentInvalid.value = !result.valid;
    documentValidationMessage.value = result.error || '';
  } else if (type === 'rut') {
    // RUT chileno: 8-9 chars (não fazemos validação pesada aqui)
    documentValid.value = digits.length >= 8;
    documentInvalid.value = !documentValid.value;
    documentValidationMessage.value = documentValid.value ? '' : t('RUT inválido');
  } else if (rule) {
    // Outros tipos com validação por comprimento mínimo
    documentValid.value = digits.length >= 6;
    documentInvalid.value = !documentValid.value;
    documentValidationMessage.value = documentValid.value ? '' : t('Documento inválido');
  } else {
    // Incompleto - remover validação
    documentValid.value = false;
    documentInvalid.value = false;
    documentValidationMessage.value = '';
  }

  // Emitir mudanças
  emit('form-update', { ...localFormData.value });
  emit('cnpj-change', documentDisplayValue.value);

  // Validar step
  const isValidStep = documentValid.value;
  emit('validation-change', isValidStep);
};

const isValid = computed<boolean>(() => {
  return documentValid.value;
});

const handleInputChange = (): void => {
  emit('form-update', { ...localFormData.value });
  emit('validation-change', isValid.value);
};

// Debounce para evitar muitas chamadas de API
let cepTimeout: ReturnType<typeof setTimeout> | null = null;

const buscarCEP = async (): Promise<void> => {
  const cepValue = localFormData.value.cep?.replace(/\D/g, '');
  if (!cepValue || cepValue.length !== 8) {
    return;
  }

  isLoadingCEP.value = true;
  const data = await lookupCep(cepValue);
  isLoadingCEP.value = false;

  if (data.erro) {
    return;
  }

  localFormData.value.endereco = data.logradouro || '';
  localFormData.value.bairro = data.bairro || '';
  localFormData.value.cidade = data.localidade || '';
  localFormData.value.estado = data.uf || '';
  localFormData.value.complemento = data.complemento || '';

  handleInputChange();
};

const handleCEPChange = (): void => {
  handleInputChange();

  // Buscar automaticamente quando o CEP tiver 8 dígitos (apenas Brasil)
  if (!isBrazil.value) return;

  const cepValue = localFormData.value.cep?.replace(/\D/g, '');

  // Limpar timeout anterior
  if (cepTimeout) {
    clearTimeout(cepTimeout);
  }

  // Buscar quando tiver 8 dígitos
  if (cepValue && cepValue.length === 8) {
    cepTimeout = setTimeout(() => {
      void buscarCEP();
    }, 500); // Aguarda 500ms após o usuário parar de digitar
  }
};

watch(() => props.formData, () => {
  syncFromProps();
}, { deep: true, immediate: true });

// Reprocessa formatação/validação ao alternar o tipo de documento (ex: Cédula ⇄ RUC no Equador)
watch(() => props.selectedDocumentType, () => {
  handleDocumentInput(documentRawValue.value);
});

onMounted(() => {
  syncFromProps();
});
</script>

<style scoped>
.wizard-step-5 {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Form Sections */
.form-section {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--surface-border);
}

.section-header {
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--surface-border);
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-field label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-color-secondary);
}

.form-field :deep(.input-text-wrapper) {
  width: 100%;
}

/* Document type selector */
.document-type-selector {
  margin-bottom: 8px;
}

.document-type-selector .field-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.doc-type-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.doc-type-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  background: var(--surface-card);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s ease;
}

.doc-type-btn:hover {
  border-color: var(--primary-color);
  background: var(--primary-50);
}

.doc-type-btn.active {
  border-color: var(--primary-color);
  background: var(--primary-100);
  color: var(--primary-color);
  font-weight: 600;
}

/* Field modifiers */
.form-field.full-width {
  grid-column: 1 / -1;
}

.form-field.narrow {
  grid-column: span 1;
}

/* Document field validation states */
.form-field.has-error :deep(.input-text-wrapper) {
  border-color: #ef4444;
}

.form-field.has-error :deep(.input-text-wrapper):focus-within {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.form-field.has-success :deep(.input-text-wrapper) {
  border-color: #22c55e;
}

.form-field.has-success :deep(.input-text-wrapper):focus-within {
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
}

.field-error {
  font-size: 12px;
  color: #ef4444;
  font-weight: 500;
}

/* CEP field special styling */
.cep-field {
  grid-column: span 1;
}

.cep-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.cep-input-wrapper :deep(.input-text-wrapper) {
  width: 100%;
}

.cep-loading {
  position: absolute;
  right: 12px;
  color: var(--primary-color);
  font-size: 16px;
  pointer-events: none;
}

/* Info Card */
.info-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 8px;
  font-size: 13px;
  color: var(--text-color);
  line-height: 1.5;
}

.info-card i {
  color: var(--primary-color);
  font-size: 18px;
  flex-shrink: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .form-field.narrow {
    grid-column: 1;
  }
}
</style>
